import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { fetchCompaniesAndWorkspaces, fetchCompanyBrandingSettings, fetchCompanyCommunicationSettings, fetchCompanyContext, fetchCompanySubscriptions, updateCompanyBrandingSettings, updateCompanyCommunicationSettings } from "@store/sdb-company/sdb-company-slice-thunk";
import { generateSdbCompanies } from "@utils/sdb-company-utils";

/**
 * State of the SdbCompany list of the user. This state contains both the workspaces and companies fetched from backend.
 * In general, SdbCompany covers both companies and admin panels (if accessible by user)
 * Therefore, this state is named SdbCompany to cover both and be distinguishable from company
 */

/** Creates an entity adapter to store a map with all the companies and workspaces that the user has access to. */
export const SdbCompaniesAdapter = createEntityAdapter({
  selectId: sdbCompany => sdbCompany.id
});

/** Initial sdbCompany state */
const initialState = {
  ...SdbCompaniesAdapter.getInitialState(),
  selectedSdbCompanyId: null,
  selectedCompanyContext: null,
  brandingSettings: null,
  communicationSettings: null,
  selectedCompanySubscriptions: [],
  fetching: {
    isFetchingSdbCompanies: false,
    fetchSelectedCompanyContext: "uninitialized",
    isFetchingCompanyBrandingSetting: false,
    isUpdatingBrandingSettings: false,
    isFetchingCompanyCommunicationSettings: false,
    isUpdatingCompanyCommunicationSettings: false,
    isFetchingCompanySubscriptions: false
  }
};

/** Slice to access the state of the company store module */
const sdbCompanySlice = createSlice({
  name: "sdbCompany",
  initialState,
  reducers: {
    /**
     * Accepts a single sdbCompany entity and adds or replaces it.
     *
     * @param state store state
     * @param entity Company to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setOne: SdbCompaniesAdapter.setOne,
    /**
     * Accepts an array of sdbCompany entities, and adds or replaces them.
     *
     * @param state store state
     * @param entity Companies to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setMany: SdbCompaniesAdapter.setMany,
    /**
     * Removes all sdbCompany entities from the store.
     *
     * @param state store state
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeAll: SdbCompaniesAdapter.removeAll,
    /**
     * Accepts an array of sdbCompany IDs, and removes each sdbCompany entity with those IDs if they exist
     *
     * @param state store state
     * @param entity Company Ids to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeMany: SdbCompaniesAdapter.removeMany,
    /**
     * Accepts a single sdbCompany IDs, and removes the sdbCompany entity with that ID if it exists.
     *
     * @param state store state
     * @param entity Company Id to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeOne: SdbCompaniesAdapter.removeOne,
    /**
     * Stores the selected sdbCompany Id.
     *
     * @param state store state.
     * @param action Company id to be set.
     */
    setSelectedSdbCompanyId(state, action) {
      state.selectedSdbCompanyId = action.payload;
    },
    /** Resets the sdb-company store to its initial state. */
    resetSdbCompanyState: () => initialState
  },
  extraReducers(builder) {
    builder.addCase(fetchCompaniesAndWorkspaces.pending, state => {
      state.fetching.isFetchingSdbCompanies = true;
    }).addCase(fetchCompaniesAndWorkspaces.fulfilled, (state, action) => {
      state.fetching.isFetchingSdbCompanies = false;
      SdbCompaniesAdapter.setMany(state, generateSdbCompanies(action.payload.companies, action.payload.workspaces));
    }).addCase(fetchCompaniesAndWorkspaces.rejected, state => {
      state.fetching.isFetchingSdbCompanies = false;
    }).addCase(fetchCompanyContext.pending, state => {
      state.fetching.fetchSelectedCompanyContext = "pending";
    }).addCase(fetchCompanyContext.fulfilled, (state, action) => {
      state.fetching.fetchSelectedCompanyContext = "succeeded";
      state.selectedCompanyContext = action.payload;
    }).addCase(fetchCompanyContext.rejected, state => {
      state.fetching.fetchSelectedCompanyContext = "rejected";
    }).addCase(fetchCompanyBrandingSettings.pending, state => {
      state.fetching.isFetchingCompanyBrandingSetting = true;
    }).addCase(fetchCompanyBrandingSettings.fulfilled, (state, action) => {
      state.fetching.isFetchingCompanyBrandingSetting = false;
      state.brandingSettings = action.payload;
    }).addCase(fetchCompanyBrandingSettings.rejected, state => {
      state.fetching.isFetchingCompanyBrandingSetting = false;
    }).addCase(updateCompanyBrandingSettings.pending, state => {
      state.fetching.isUpdatingBrandingSettings = true;
    }).addCase(updateCompanyBrandingSettings.fulfilled, (state, action) => {
      state.brandingSettings = action.payload;
      state.fetching.isUpdatingBrandingSettings = false;
    }).addCase(updateCompanyBrandingSettings.rejected, state => {
      state.fetching.isUpdatingBrandingSettings = false;
    }).addCase(fetchCompanyCommunicationSettings.pending, state => {
      state.fetching.isFetchingCompanyCommunicationSettings = true;
    }).addCase(fetchCompanyCommunicationSettings.fulfilled, (state, action) => {
      state.communicationSettings = action.payload;
      state.fetching.isFetchingCompanyCommunicationSettings = false;
    }).addCase(fetchCompanyCommunicationSettings.rejected, state => {
      state.fetching.isFetchingCompanyCommunicationSettings = false;
    }).addCase(updateCompanyCommunicationSettings.pending, state => {
      state.fetching.isUpdatingCompanyCommunicationSettings = true;
    }).addCase(updateCompanyCommunicationSettings.fulfilled, (state, action) => {
      state.communicationSettings = action.payload;
      state.fetching.isUpdatingCompanyCommunicationSettings = false;
    }).addCase(updateCompanyCommunicationSettings.rejected, state => {
      state.fetching.isUpdatingCompanyCommunicationSettings = false;
    }).addCase(fetchCompanySubscriptions.pending, state => {
      state.fetching.isFetchingCompanySubscriptions = true;
    }).addCase(fetchCompanySubscriptions.fulfilled, (state, action) => {
      state.selectedCompanySubscriptions = action.payload;
      state.fetching.isFetchingCompanySubscriptions = false;
    }).addCase(fetchCompanySubscriptions.rejected, state => {
      state.fetching.isFetchingCompanySubscriptions = false;
    });
  }
});
export const {
  setOne,
  setMany,
  removeAll,
  removeMany,
  removeOne,
  setSelectedSdbCompanyId,
  resetSdbCompanyState
} = sdbCompanySlice.actions;
export const sdbCompanyReducer = sdbCompanySlice.reducer;